import type {
  FunctionComponent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from 'react';
import { useCallback } from 'react';
import classNames from 'classnames';

import { Button } from '@trello/nachos/button';
import { Tooltip } from '@trello/nachos/tooltip';
import type { NotificationTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { Emoji } from 'app/src/components/Emoji';
import type { ReactionModel } from './Reactions.types';

import styles from './Reaction.less';

type ReactionOwnProps = {
  reaction: ReactionModel;
  count: number;
  isMyReaction?: boolean;
  isDisabled?: boolean;
  onClick?: (
    emoji: ReactionModel,
    e: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
  tooltip?: ReactNode;
};

type ReactionProps = ReactionOwnProps;

export const Reaction: FunctionComponent<ReactionProps> = ({
  reaction,
  count,
  isMyReaction = false,
  isDisabled = false,
  onClick,
  tooltip,
}) => {
  const { emoji } = reaction;

  const _onClick = useCallback(
    (e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (isDisabled || !onClick) {
        return;
      }

      onClick(reaction, e);
    },
    [isDisabled, reaction, onClick],
  );

  const renderedReaction = (
    <Button
      className={classNames(styles.reaction, {
        [styles.myReaction]: isMyReaction,
        [styles.disabled]: isDisabled,
      })}
      onClick={_onClick}
      role={onClick && 'button'}
      data-testid={`${getTestId<NotificationTestIds>(
        'notification-reaction-',
      )}${emoji.shortcodes}`}
    >
      <Emoji emoji={emoji.shortcodes} />
      <div className={styles.reactionCount}>{count > 99 ? '99+' : count}</div>
    </Button>
  );

  return (
    <>
      {tooltip && !isDisabled ? (
        <Tooltip content={tooltip}>{renderedReaction}</Tooltip>
      ) : (
        renderedReaction
      )}
    </>
  );
};
